import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    FileSignature,
    Upload,
    Loader2,
    Trash2,
    Download,
    Pencil,
    AlertTriangle,
    BookOpen,
    ChevronDown,
    Euro,
    Save,
} from "lucide-react";
import { toast } from "sonner";

export default function ContractTemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [name, setName] = useState("");
    const [kind, setKind] = useState("template");
    const [file, setFile] = useState(null);
    const [guideOpen, setGuideOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null); // {id, name}
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [rates, setRates] = useState(null);
    const [ratesOpen, setRatesOpen] = useState(false);
    const [savingRates, setSavingRates] = useState(false);
    const fileRef = useRef(null);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [tplRes, phRes, ratesRes] = await Promise.all([
                api.get("/contract-templates"),
                api.get("/contract-templates/placeholders"),
                api.get("/contract-templates/ccnl-rates").catch(() => ({ data: null })),
            ]);
            setTemplates(tplRes.data);
            setPlaceholders(phRes.data);
            setRates(ratesRes.data);
        } catch {
            toast.error("Errore nel caricamento dei modelli");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleUpload = async () => {
        if (!file) {
            toast.error("Seleziona un file Word (.docx)");
            return;
        }
        if (!name.trim()) {
            toast.error("Dai un nome al modello (es. Contratto Determinato Part-time)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("name", name.trim());
            fd.append("kind", kind);
            await api.post("/contract-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Modello caricato");
            setName("");
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nel caricamento");
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (t) => {
        try {
            const res = await api.get(`/contract-templates/${t.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${t.name}.docx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Errore nel download");
        }
    };

    const handleRename = async () => {
        if (!renameTarget?.name?.trim()) return;
        try {
            await api.put(`/contract-templates/${renameTarget.id}`, { name: renameTarget.name.trim() });
            toast.success("Modello rinominato");
            setRenameTarget(null);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/contract-templates/${deleteTarget.id}`);
            toast.success("Modello eliminato");
            setDeleteTarget(null);
            fetchAll();
        } catch {
            toast.error("Errore nell'eliminazione");
        }
    };

    const handleSaveRates = async () => {
        setSavingRates(true);
        try {
            const { data } = await api.put("/contract-templates/ccnl-rates", rates);
            setRates(data);
            toast.success("Tabelle CCNL aggiornate");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore nel salvataggio delle tabelle");
        } finally {
            setSavingRates(false);
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8" data-testid="contract-templates-page">
            <div className="mb-6">
                <h1 className="flex items-center gap-2 font-display text-3xl font-bold text-[#376DB3]">
                    <FileSignature className="h-7 w-7" />
                    Modelli contratto
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                    Carica i tuoi modelli Word (.docx) con i segnaposto: dalla scheda dipendente potrai generare il
                    contratto già compilato in Word o PDF.
                </p>
            </div>

            {/* Upload */}
            <div className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="mb-3 font-display text-base font-semibold text-slate-800">Carica un nuovo modello</h2>
                <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[220px] flex-1">
                        <Label htmlFor="tpl-name">Nome del modello</Label>
                        <Input
                            id="tpl-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Es. Contratto Tempo Determinato Part-time"
                            className="mt-1.5"
                            data-testid="template-name-input"
                        />
                    </div>
                    <div className="w-56">
                        <Label>Tipo di modello</Label>
                        <Select value={kind} onValueChange={setKind}>
                            <SelectTrigger className="mt-1.5" data-testid="template-kind-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="template">Contratto</SelectItem>
                                <SelectItem value="allegato">Allegato disciplinare</SelectItem>
                                <SelectItem value="proroga">Comunicazione proroga T.D.</SelectItem>
                                <SelectItem value="trasformazione">Comunicazione trasformazione T.I.</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <input
                            type="file"
                            accept=".docx"
                            ref={fileRef}
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="template-file-input"
                        />
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileRef.current?.click()}
                            className="gap-2"
                            data-testid="template-pick-file-btn"
                        >
                            <Upload className="h-4 w-4" />
                            {file ? file.name : "Scegli file .docx"}
                        </Button>
                    </div>
                    <Button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="template-upload-btn"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Carica modello
                    </Button>
                </div>
            </div>

            {/* Placeholder guide */}
            <div className="mb-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                <button
                    type="button"
                    onClick={() => setGuideOpen((v) => !v)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left"
                    data-testid="placeholder-guide-toggle"
                >
                    <span className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                        <BookOpen className="h-4 w-4 text-[#376DB3]" />
                        Guida ai segnaposto
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${guideOpen ? "rotate-180" : ""}`} />
                </button>
                {guideOpen && (
                    <div className="border-t border-slate-100 px-5 py-4" data-testid="placeholder-guide-content">
                        <p className="mb-3 text-sm text-slate-600">
                            Nel tuo file Word scrivi il segnaposto tra doppie graffe, es.{" "}
                            <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-[#376DB3]">{"{{cognome}}"}</code>
                            . Alla generazione verrà sostituito con il dato del dipendente.
                        </p>
                        <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
                            {placeholders.map((p) => (
                                <div key={p.key} className="flex items-center justify-between gap-2 border-b border-slate-50 py-1 text-sm">
                                    <code className="font-mono text-xs text-[#376DB3]">{`{{${p.key}}}`}</code>
                                    <span className="text-right text-xs text-slate-500">{p.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* CCNL pay tables */}
            <div className="mb-6 rounded-lg border border-slate-200 bg-white shadow-sm">
                <button
                    type="button"
                    onClick={() => setRatesOpen((v) => !v)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left"
                    data-testid="ccnl-rates-toggle"
                >
                    <span className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                        <Euro className="h-4 w-4 text-[#376DB3]" />
                        Tabelle retributive CCNL
                        {rates?.effective_from && (
                            <Badge variant="outline" className="ml-1 border-slate-200 font-mono-numbers text-[10px] text-slate-500">
                                dal {rates.effective_from.split("-").reverse().join("/")}
                            </Badge>
                        )}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${ratesOpen ? "rotate-180" : ""}`} />
                </button>
                {ratesOpen && rates && (
                    <div className="border-t border-slate-100 px-5 py-4" data-testid="ccnl-rates-content">
                        <p className="mb-3 text-sm text-slate-600">
                            Valori <b>mensili lordi full-time (40 ore)</b> del CCNL Imprese di pulizia/Multiservizi, usati per
                            calcolare le retribuzioni nei contratti (riproporzionati per il part-time). Aggiornali qui quando il
                            CCNL si rinnova.
                        </p>
                        <div className="mb-3 flex items-center gap-2">
                            <Label className="text-xs">In vigore dal</Label>
                            <Input
                                type="date"
                                value={rates.effective_from || ""}
                                onChange={(e) => setRates((r) => ({ ...r, effective_from: e.target.value }))}
                                className="h-8 w-40"
                                data-testid="ccnl-effective-input"
                            />
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                                        <th className="py-2 pr-3">Livello</th>
                                        <th className="py-2 pr-3">Paga base</th>
                                        <th className="py-2 pr-3">Contingenza</th>
                                        <th className="py-2 pr-3">E.D.R.</th>
                                        <th className="py-2">Anzianità</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {["1", "2", "3", "4"].map((lvl) => (
                                        <tr key={lvl} className="border-b border-slate-50">
                                            <td className="py-1.5 pr-3 font-semibold text-slate-700">{lvl}°</td>
                                            {["paga_base", "contingenza", "edr", "anzianita"].map((f) => (
                                                <td key={f} className="py-1.5 pr-3">
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={rates.levels?.[lvl]?.[f] ?? ""}
                                                        onChange={(e) =>
                                                            setRates((r) => ({
                                                                ...r,
                                                                levels: { ...r.levels, [lvl]: { ...r.levels[lvl], [f]: e.target.value } },
                                                            }))
                                                        }
                                                        className="h-8 w-28 font-mono-numbers"
                                                        data-testid={`ccnl-input-${lvl}-${f}`}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-3 flex justify-end">
                            <Button
                                onClick={handleSaveRates}
                                disabled={savingRates}
                                className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                                data-testid="ccnl-save-btn"
                            >
                                {savingRates ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                Salva tabelle
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Templates list */}
            <div className={`flex justify-center py-8 ${loading ? "" : "hidden"}`}>
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
            <div className={`rounded-lg border border-dashed border-slate-300 bg-slate-50 py-10 text-center text-sm text-slate-500 ${!loading && templates.length === 0 ? "" : "hidden"}`} data-testid="templates-empty">
                Nessun modello caricato. Carica il tuo primo modello Word qui sopra.
            </div>
            <div className="space-y-3">
                {templates.map((t) => (
                    <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid={`template-card-${t.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                                    {t.name}
                                    {t.kind === "allegato" && (
                                        <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">Allegato</Badge>
                                    )}
                                    {t.kind === "proroga" && (
                                        <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Comunicazione proroga</Badge>
                                    )}
                                    {t.kind === "trasformazione" && (
                                        <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">Comunicazione trasformazione</Badge>
                                    )}
                                </h3>
                                <p className="mt-0.5 text-xs text-slate-500">
                                    {t.filename} · {t.size_kb} KB · {t.placeholders.length} segnaposto rilevati
                                </p>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1.5">
                                <Button size="sm" variant="outline" onClick={() => handleDownload(t)} className="gap-1.5" data-testid={`template-download-${t.id}`}>
                                    <Download className="h-3.5 w-3.5" /> Scarica
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setRenameTarget({ id: t.id, name: t.name })} className="gap-1.5" data-testid={`template-rename-${t.id}`}>
                                    <Pencil className="h-3.5 w-3.5" /> Rinomina
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(t)} className="gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`template-delete-${t.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>
                        {t.placeholders.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {t.placeholders.map((p) => (
                                    <Badge
                                        key={p}
                                        variant="outline"
                                        className={
                                            t.unknown_placeholders.includes(p)
                                                ? "border-amber-300 bg-amber-50 font-mono text-[10px] text-amber-700"
                                                : "border-slate-200 bg-slate-50 font-mono text-[10px] text-slate-600"
                                        }
                                    >
                                        {`{{${p}}}`}
                                    </Badge>
                                ))}
                            </div>
                        )}
                        {t.unknown_placeholders.length > 0 && (
                            <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700" data-testid={`template-unknown-warning-${t.id}`}>
                                <AlertTriangle className="h-3.5 w-3.5" />
                                I segnaposto evidenziati in giallo non sono riconosciuti e resteranno vuoti (vedi Guida ai segnaposto).
                            </p>
                        )}
                    </div>
                ))}
            </div>

            {/* Rename dialog */}
            <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
                <DialogContent data-testid="template-rename-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Rinomina modello</DialogTitle>
                    </DialogHeader>
                    <Input
                        value={renameTarget?.name || ""}
                        onChange={(e) => setRenameTarget((v) => ({ ...v, name: e.target.value }))}
                        data-testid="template-rename-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameTarget(null)}>Annulla</Button>
                        <Button onClick={handleRename} className="bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="template-rename-confirm">
                            Salva
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete dialog */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent data-testid="template-delete-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-red-700">Elimina modello</DialogTitle>
                        <DialogDescription>
                            Eliminare definitivamente «{deleteTarget?.name}»? L'operazione non è reversibile.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
                        <Button onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700" data-testid="template-delete-confirm">
                            Elimina
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
